// analytics.js — envoltorio fino sobre posthog-js para el piloto (Pablo,
// 2–13 nov 2026). Objetivo: entender flujos/uso real, nunca controlar a
// nadie — por eso cada función de aquí es un no-op seguro si PostHog no
// está configurado, y nunca lanza (se llama directamente desde el cierre de
// visita del técnico — un fallo de PostHog no puede romper eso, mismo
// criterio que reportClientError()/errorReporting.js).
//
// posthog-js se carga con import() dinámico, no estático: la app no hace
// code-splitting por pantalla todavía, así que este es el primer chunk
// separado del bundle principal — sin esto, el SDK (y rrweb si se activa la
// grabación) viajarían dentro de main.js y pesarían en el arranque de todo
// el mundo, no solo de quien está en el piloto.
//
// Activación: SOLO si existe REACT_APP_POSTHOG_KEY, y solo si esa variable
// está puesta en Netlify — se deja a propósito fuera de netlify.toml y se
// configura por contexto en el panel de Netlify (Production únicamente), así
// un único mecanismo cubre a la vez el "desactivable por variable de
// entorno" y el "nada se envía desde staging": staging/local simplemente no
// tienen la variable.
//
// Grabación de sesión activada por defecto durante el piloto (apagarla no
// debe depender de acordarse de poner una variable nueva en Netlify — eso ya
// nos pasó una vez). REACT_APP_POSTHOG_SESSION_RECORDING=false la apaga sola
// cuando acabe el piloto, sin tocar el resto de eventos.

import { esRutaPublica } from '../utils/publicRoutes';

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const DISABLED = process.env.REACT_APP_POSTHOG_DISABLED === 'true';
const SESSION_RECORDING = process.env.REACT_APP_POSTHOG_SESSION_RECORDING !== 'false';

// Nunca en una ruta pública (firma de presupuestos/contratos, sin login,
// abierta por el cliente): PostHog grabó sesiones reales de cliente en
// /firmar/<id> porque nada comprobaba la ruta antes de iniciar — fallo de
// privacidad real, no solo de piloto (Rafa, 2026-11-05). Se evalúa una sola
// vez al cargar el módulo porque esta SPA no tiene router: el path de
// window.location en ese momento es la página que el navegador tiene
// abierta de verdad.
const EN_RUTA_PUBLICA = typeof window !== 'undefined' && esRutaPublica();

export const ANALYTICS_ENABLED = !DISABLED && !!POSTHOG_KEY && !EN_RUTA_PUBLICA;

let posthogPromise = null;
function loadPosthog() {
  if (!posthogPromise) {
    posthogPromise = import('posthog-js').then(m => m.default || m);
  }
  return posthogPromise;
}

let initialized = false;

export function initAnalytics() {
  if (!ANALYTICS_ENABLED || initialized) return;
  initialized = true;
  loadPosthog().then(posthog => {
    try {
      posthog.init(POSTHOG_KEY, {
        api_host: 'https://eu.i.posthog.com',
        // 'identified_only' descartaba el enlace a persona de cualquier
        // evento disparado antes de que identify() completara (autocapture
        // de los primeros clics tras cargar) — en 'always' PostHog crea el
        // perfil desde el principio y lo fusiona al identificar, así ningún
        // evento se queda "anónimo" para siempre (Rafa, 2026-11-03).
        person_profiles: 'always',
        // Autocapture SIN texto ni atributos con datos (Rafa, 2026-11-05):
        // guardaba el texto de cada elemento pulsado ($el_text) y con él
        // direcciones completas y descripciones de avería con notas
        // personales. mask_all_text quita ese texto de todos los clics; los
        // botones se identifican por su data-attr (ver los de la pantalla de
        // orden). No se usa mask_all_element_attributes porque descarta
        // TODOS los atributos, data-attr incluido — en su lugar se ignoran
        // solo los que pueden llevar datos de cliente: href (tel:…, enlaces
        // a Maps con la dirección, wa.me con el teléfono), title,
        // aria-label, alt, placeholder, value y src. Comprobado contra el
        // código de posthog-js 1.444.4.
        mask_all_text: true,
        autocapture: {
          element_attribute_ignorelist: ['href', 'title', 'aria-label', 'alt', 'placeholder', 'value', 'src'],
        },
        // No hay React Router — la navegación es un switch sobre estado
        // local, así que el pageview automático (basado en la History API)
        // no vería casi nada. Se manda a mano desde capturePageview().
        capture_pageview: false,
        disable_session_recording: !SESSION_RECORDING,
        session_recording: {
          // El enmascarado por defecto de PostHog solo tapa
          // <input>/<textarea> — aquí el nombre/teléfono del cliente se
          // muestra como texto plano en tarjetas de orden, CRM,
          // mantenimientos... maskTextSelector:'*' tapa TODO nodo de texto
          // del DOM en la grabación, no solo los campos de formulario.
          // Imprescindible dado el requisito de "sin datos personales de
          // clientes" (Rafa, 2026-11-02).
          maskTextSelector: '*',
          maskAllInputs: true,
          // Los ATRIBUTOS no los tapa maskTextSelector (un href="tel:…" se
          // grabaría tal cual). Para eso, los contenedores con datos de
          // cliente llevan la clase ph-no-capture: en la grabación se
          // bloquean enteros (recuadro vacío), y el autocapture ignora los
          // clics dentro. No se usa maskAllElementAttributes aquí: tapa
          // también `class` y la grabación saldría sin estilos.
        },
      });
    } catch { /* nunca debe romper el arranque de la app */ }
  }).catch(() => {});
}

/** Identifica al usuario logueado con su rol — null-safe, no-op sin PostHog. */
export function identifyUser(user, { isAdminImpersonating = false } = {}) {
  if (!ANALYTICS_ENABLED || !user) return;
  initAnalytics();
  loadPosthog().then(posthog => {
    try {
      posthog.identify(String(user.employeeId ?? user.email ?? user.id ?? 'desconocido'), {
        role: user.role || 'desconocido',
        isAdminImpersonating,
      });
    } catch { /* best-effort */ }
  }).catch(() => {});
}

/** Cierre de sesión — corta la identidad para que el próximo login no herede eventos. */
export function resetAnalytics() {
  if (!ANALYTICS_ENABLED || !initialized) return;
  loadPosthog().then(posthog => {
    try { posthog.reset(); } catch { /* best-effort */ }
  }).catch(() => {});
}

/**
 * Pageview manual — screen es el nombre de pantalla/pestaña activa (no hay
 * URL real, es un switch sobre estado). Se manda también una $current_url/
 * $pathname sintéticas: sin esto, los informes nativos de PostHog (rutas,
 * flujos, heatmaps) agrupan por $current_url y, como la URL real del
 * navegador nunca cambia, todas las pantallas aparecían como "/" (Rafa,
 * 2026-11-03). Con barras en vez de ":" para que se lean como rutas de
 * verdad, y window.location.origin (nunca un dominio fijo) para que
 * staging, si algún día llegara a enviar algo, no se mezcle con producción
 * bajo la misma URL (Rafa, 2026-11-04).
 */
export function capturePageview(screen) {
  if (!ANALYTICS_ENABLED || !screen) return;
  loadPosthog().then(posthog => {
    try {
      const path = `/${screen.replace(/:/g, '/')}`;
      posthog.capture('$pageview', {
        screen,
        $current_url: `${window.location.origin}${path}`,
        $pathname: path,
      });
    } catch { /* best-effort */ }
  }).catch(() => {});
}

/**
 * orden_id para los eventos de orden (orden_abierta, orden_paso_completado,
 * orden_cerrada, orden_abandonada, salida_a_stelorder) — el MISMO valor en
 * todos, para poder seguir una orden de principio a fin en PostHog. Es el id
 * del documento en Firebase (`_firebaseId`, que en las órdenes de StelOrder
 * coincide con su id numérico); `id` solo de reserva, porque en una orden
 * creada desde la app que aún no está en StelOrder vale 0
 * (getTechnicianOrders.js). Identificador interno, no dato del cliente.
 */
export function ordenIdAnalytics(order) {
  const id = order?._firebaseId || order?.id;
  return id ? String(id) : null;
}

/**
 * trackEvent — nunca de datos personales de cliente (nombre/dirección/
 * teléfono/importe) en `props`; solo ids, enums y duraciones. Se llama desde
 * flujos reales (cierre de visita, alta de incidencia) — jamás debe lanzar.
 */
export function trackEvent(name, props = {}) {
  if (!ANALYTICS_ENABLED) return;
  loadPosthog().then(posthog => {
    try { posthog.capture(name, props); } catch { /* best-effort */ }
  }).catch(() => {});
}
